import MapView from '@arcgis/core/views/MapView'
import FeatureLayer from '@arcgis/core/layers/FeatureLayer'
import LayerBaseService from '@/components/service/layer/layerBaseService'
import { layerProps } from '@/components/service/layer/pressure-sensor-point/layerProps'
import { ILayer } from '@/components/service/interface/layerInterface'
import Point from '@arcgis/core/geometry/Point'
import Graphic from '@arcgis/core/Graphic'
import { IDrawableLayer } from '@/components/service/interface/drawableLayerInterface'
import { ServiceContainer } from '@/components/service/serviceContainer'

interface DrawParams {
	pressureSensors: any
}

const PRESSURE_SENSOR_THRESHOLD = 20

export class PressureSensorPointLayerService extends LayerBaseService implements ILayer, IDrawableLayer {
	private layer!: FeatureLayer
	private cachedDrawData: DrawParams = { pressureSensors: {} }

	constructor(mapView: MapView, serviceContainer: ServiceContainer) {
		super(mapView, serviceContainer)
	}

	getLayer() {
		return this.layer
	}

	init() {
		this.layer = new FeatureLayer(layerProps)
	}

	load() {
		this.mapView.map.add(this.layer)
	}

	async draw(params: DrawParams) {
		if (!this.shouldDraw(params)) {
			return
		}

		const { pressureSensors } = params

		if (!pressureSensors.childDataStructures) {
			return
		}

		await this.removeAllFeatures()

		const graphics: Graphic[][] = pressureSensors.childDataStructures.map((childDataStructure: any) => {
			if (childDataStructure && childDataStructure.childDataStructures) {
				const graphics: Graphic[] = childDataStructure.childDataStructures
					.filter((grandChildDataStructure: any) => {
						const lastItemYValue = grandChildDataStructure.data?.findLast((item: any) => !!item.y)?.y
						return lastItemYValue <= PRESSURE_SENSOR_THRESHOLD
					})
					.map((grandChildDataStructure: any) => {
						const point = new Point({
							y: parseFloat(grandChildDataStructure.args.latitude),
							x: parseFloat(grandChildDataStructure.args.longitude),
							spatialReference: { wkid: 4326 }
						})
						return new Graphic({
							attributes: {
								OBJECTID: grandChildDataStructure.id
							},
							geometry: point
						})
					})

				return graphics
			}
		})

		await this.layer.applyEdits({ addFeatures: graphics.flat() })
		this.layer.refresh()
	}

	async removeAllFeatures() {
		return super.removeAllFeatures(this.layer)
	}

	isLoaded() {
		return this.mapView.map.layers.includes(this.layer)
	}

	shouldDraw(drawData: DrawParams): boolean {
		const isLayerVisible = this.layer.visible

		const draw = this.isLoaded() || isLayerVisible

		if (!draw) {
			this.cachedDrawData = drawData
		}

		return draw
	}

	visible(visible: boolean) {
		this.layer.visible = visible

		if (visible && !this.isLoaded()) {
			this.load()
			this.draw(this.cachedDrawData)
		}
	}
}
